<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import {
  DEF_CALLS_PATH,
  INCOMINGS_PATH,
  MY_PLAN_PATH,
  PUSH_REQUESTS_PATH,
} from '@/composables/useGroupBadges'
import { normalizeItemPath, normalizeMenuPath, useMenuStore } from '@/stores/menu'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

/**
 * Bottom bar of the phone layout (below md): the four group pages people
 * reach for during an attack, one tap away instead of inside the drawer.
 * Only with a selected group, since every one of them is about that group.
 *
 * The numbers are the menu's own (`pending_count`, local counts included), so
 * the bar and the sidebar can never disagree: there is no request of its own.
 */
const ENTRIES = [
  { path: INCOMINGS_PATH, icon: 'mdi-sword', label: 'mobileNav.incomings' },
  { path: DEF_CALLS_PATH, icon: 'mdi-wall', label: 'mobileNav.defCalls' },
  { path: MY_PLAN_PATH, icon: 'mdi-clipboard-text', label: 'mobileNav.yourPlan' },
  { path: PUSH_REQUESTS_PATH, icon: 'mdi-cart', label: 'mobileNav.pushRequests' },
] as const

const { t } = useI18n()
const auth = useAuthStore()
const user = useUserStore()
const menu = useMenuStore()

/** Badge count by normalised path, read from the visible menu tree. */
const counts = computed(() => {
  const byPath = new Map<string, number>()
  for (const { item } of menu.flatVisibleEntries) {
    const path = normalizeItemPath(item.path)
    if (path !== null && item.pending_count) byPath.set(path, item.pending_count)
  }
  return byPath
})

/** Like the home's shortcuts: no entry leads to a page the menu doesn't offer. */
const entries = computed(() =>
  ENTRIES.filter((entry) => menu.isAllowed(entry.path)).map((entry) => ({
    ...entry,
    count: counts.value.get(normalizeMenuPath(entry.path)) ?? 0,
  })),
)

const visible = computed(
  () => auth.isAuthenticated && user.selectedGroup !== null && entries.value.length > 0,
)
</script>

<template>
  <template v-if="visible">
    <!-- In-flow spacer: the fixed bar must not cover the end of the page. -->
    <div class="h-16 shrink-0 md:hidden" aria-hidden="true" />

    <nav
      class="chrome-surface border-gold-600/60 fixed inset-x-0 bottom-0 z-40 border-t-2 pb-[env(safe-area-inset-bottom)] shadow-raised md:hidden"
      :aria-label="t('mobileNav.label')"
    >
      <ul class="flex h-16 items-stretch">
        <li v-for="entry in entries" :key="entry.path" class="min-w-0 flex-1">
          <RouterLink :to="entry.path" custom>
            <template #default="{ navigate, href, isActive }">
              <a
                :href="href"
                class="flex h-full flex-col items-center justify-center gap-0.5 px-1 text-[11px] leading-tight transition-colors"
                :class="
                  isActive
                    ? 'text-gold-300 bg-white/10'
                    : 'text-parchment-200 hover:bg-white/10 hover:text-white'
                "
                :aria-current="isActive ? 'page' : undefined"
                @click="navigate"
              >
                <span class="relative">
                  <AppIcon :name="entry.icon" class="text-2xl" />
                  <span
                    v-if="entry.count"
                    class="badge absolute -top-1.5 -right-3 min-w-5 justify-center bg-red-600 px-1 text-[10px] leading-4 text-white ring-2 ring-abyss-900"
                  >
                    {{ entry.count > 99 ? '99+' : entry.count }}
                  </span>
                </span>
                <span class="w-full truncate text-center">{{ t(entry.label) }}</span>
              </a>
            </template>
          </RouterLink>
        </li>
      </ul>
    </nav>
  </template>
</template>
